import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBalanceSheetReport } from '../../../../redux/actions/SocietyPortal/AccountReportsAction/AccountReportsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import ReportShell, { ReportActionButtons } from '../components/ReportShell';
import { FinancialYearField, DateField, GetReportButton } from '../components/ReportFilters';
import TwoColumnReport from '../components/TwoColumnReport';
import { getCurrentFY, fyDateRange } from '../components/helpers';
import { exportBalanceSheet } from '../components/reportExcel';

const BalanceSheet = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { bsData, bsLoading } = useSelector(state => state.AccountReportsReducer || {});

    const [financialYear, setFinancialYear] = useState(getCurrentFY());
    const [asAtDate, setAsAtDate] = useState(fyDateRange(getCurrentFY()).to);
    const [collapseSignal, setCollapseSignal] = useState(0);

    useEffect(() => {
        setAsAtDate(fyDateRange(financialYear).to);
    }, [financialYear]);

    const handleGet = () => {
        if (!societyId) return;
        dispatch(getBalanceSheetReport({ societyId, toDate: asAtDate }));
    };
    usePageRefresh(handleGet);

    useEffect(() => { handleGet(); /* eslint-disable-next-line */ }, [societyId]);

    const columns = [
        { label: 'Current Year' },
        { label: 'Previous Year', muted: true },
    ];

    const assets = bsData?.assets || {};
    const liabilities = bsData?.liabilities || {};
    const assetsGroups = assets.groups || [];
    const liabilitiesGroups = liabilities.groups || [];

    const totalAssetsCurrent = parseFloat(assets.total?.current) || 0;
    const totalAssetsPrevious = parseFloat(assets.total?.previous) || 0;
    const totalLiabilitiesCurrent = parseFloat(liabilities.total?.current) || 0;
    const totalLiabilitiesPrevious = parseFloat(liabilities.total?.previous) || 0;

    // Profit on liabilities (when positive) | Loss on assets (when negative)
    const liabProfit = liabilities.profit;
    const assetLoss = assets.loss;

    const liabilitiesExtra = liabProfit
        ? {
            label: 'Profit for the Year',
            current: parseFloat(liabProfit.current) || 0,
            previous: parseFloat(liabProfit.previous) || 0,
        }
        : null;

    const assetsExtra = assetLoss
        ? {
            label: 'Loss for the Year',
            current: parseFloat(assetLoss.current) || 0,
            previous: parseFloat(assetLoss.previous) || 0,
        }
        : null;

    const handleExcel = () => exportBalanceSheet(bsData, [
        {
            title: 'Liabilities',
            nodes: liabilitiesGroups,
            extraRow: liabilitiesExtra,
            totalLabel: 'Total Liabilities',
            totalCurrent: totalLiabilitiesCurrent,
            totalPrevious: totalLiabilitiesPrevious,
        },
        {
            title: 'Assets',
            nodes: assetsGroups,
            extraRow: assetsExtra,
            totalLabel: 'Total Assets',
            totalCurrent: totalAssetsCurrent,
            totalPrevious: totalAssetsPrevious,
        },
    ]);

    return (
        <ReportShell
            title="Balance Sheet"
            actions={<ReportActionButtons
                onCollapseAll={() => setCollapseSignal((s) => s + 1)}
                onExcel={bsData ? handleExcel : undefined}
                onPrint={() => window.print()}
            />}
            filters={(
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-items-end">
                    <FinancialYearField value={financialYear} onChange={setFinancialYear} />
                    <DateField label="As at Date" value={asAtDate} onChange={setAsAtDate} />
                    <div></div>
                    <GetReportButton onClick={handleGet} loading={bsLoading} />
                </div>
            )}
        >
            {bsLoading && !bsData ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-16">
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                        <span className="tw-text-xs tw-text-gray-400">Loading balance sheet...</span>
                    </div>
                </div>
            ) : (
                <TwoColumnReport
                    columns={columns}
                    leftTitle="Liabilities"
                    leftNodes={liabilitiesGroups}
                    leftTotalLabel="Total Liabilities"
                    leftTotalCurrent={totalLiabilitiesCurrent}
                    leftTotalPrevious={totalLiabilitiesPrevious}
                    leftExtraRow={liabilitiesExtra}
                    rightTitle="Assets"
                    rightNodes={assetsGroups}
                    rightTotalLabel="Total Assets"
                    rightTotalCurrent={totalAssetsCurrent}
                    rightTotalPrevious={totalAssetsPrevious}
                    rightExtraRow={assetsExtra}
                    collapseAllSignal={collapseSignal}
                />
            )}
        </ReportShell>
    );
};

export default BalanceSheet;
